import React, { FC, useEffect, useState } from "react";
import { MySelect } from "pages/StepPages/StepTwo/MySelect/MySelect";
import { MyInput } from "pages/StepPages/StepTwo/MyInput/MyInput";
import { Button } from "components/shared";
import { BUTTON_VARIANTS } from "components/shared/Button/Button";
import { MyTextarea } from "pages/StepPages/StepTwo/MyTextarea/MyTextarea";
import { IOptionalFields, IRequiredFields } from "./types";
import InputTemp from "components-new/InputTemp";
import { useAppDispatch, useAppSelector } from "toolkit/hooks";
import { editOrganization } from "toolkit/organizations/organizations.thunks";
import { connectNames, replacePhone } from "helpers";
import styles from "./EditionOrganization.module.sass";
import { INN_LIMIT, KPP_LIMIT, OGRN_LIMIT } from "constant";
import { directoryAPI } from "libs/apiClient/Directory";
import { ActivityTypeSimpleResponse, OrganizationTypeResponse } from "libs/apiClient/data-contracts";

interface EditionOrganizationProps {
	close: () => void;
}

export const EditionOrganization: FC<EditionOrganizationProps> = ({ close }) => {
	const dispatch = useAppDispatch();
	const [submitError, setSubmitError] = useState<boolean>(false);
	const { selectedOrganization } = useAppSelector((state) => state.organizations);
	const [loading, setLoading] = useState<boolean>(false);

	const [requiredFields, setRequiredFields] = useState<IRequiredFields>({
		shortName: "",
		type: null,
		fullName: "",
		parent: null,
	});

	const [optionalFields, setOptionalFields] = useState<IOptionalFields>({
		propertyType: null,
		phone: "",
		legalAddress: "",
		postalAddress: "",
		website: "",
		activityArea: null,
		responsible: null,
		director: null,
		inn: "",
		kpp: "",
		ogrn: "",
	});

	const [selectData, setSelectData] = useState<any>({
		parentList: [],
		hierarchyTypeList: [],
		typeList: [],
		activityAreaList: [],
		CEOList: [],
	});

	useEffect(() => {
		setRequiredFields((prev) => ({
			...prev,
			shortName: selectedOrganization!.shortName,
			fullName: selectedOrganization!.fullName,
			parent: selectedOrganization?.parent
				? { id: selectedOrganization.parent.id, name: selectedOrganization.parent.shortName }
				: { id: 0, name: "" },
			type: { id: selectedOrganization!.type.id, name: selectedOrganization!.type.name },
		}));
		setOptionalFields({
			...optionalFields,
			propertyType: selectedOrganization?.propertyType
				? { id: selectedOrganization?.propertyType.id, name: selectedOrganization?.propertyType.type }
				: null,
			activityArea: selectedOrganization?.activityArea || null,
			responsible: selectedOrganization?.responsible
				? {
						id: selectedOrganization?.responsible.id,
						name: `${selectedOrganization?.responsible.lastName} ${selectedOrganization?.responsible.firstName} ${selectedOrganization?.responsible.middleName}`,
				  }
				: null,
			director: selectedOrganization?.director
				? {
						id: selectedOrganization?.director.id,
						name: `${selectedOrganization?.director.lastName} ${selectedOrganization?.director.firstName} ${selectedOrganization?.director.middleName}`,
				  }
				: null,
			inn: selectedOrganization!.inn || "",
			kpp: selectedOrganization!.kpp || "",
			ogrn: selectedOrganization!.ogrn || "",
			phone: replacePhone(selectedOrganization!.phone, "to_front") || "",
			legalAddress: selectedOrganization!.legalAddress || "",
			postalAddress: selectedOrganization!.postalAddress || "",
			website: selectedOrganization!.website || "",
		});
		//eslint-disable-next-line
	}, []);

	useEffect(() => {
		if (!requiredFields.type?.id) return;
		directoryAPI.organizationsParentList({ id: requiredFields.type?.id }).then((r) => {
			if (r.data.length) {
				const newParents: any[] = [];
				// eslint-disable-next-line array-callback-return
				r.data.map((item: any) => {
					newParents.push({ id: item.id, name: item.shortName });
				});
				setSelectData((prev: any) => ({ ...prev, parentList: newParents }));
			}
		});

		if (
			(selectedOrganization?.type.name === "Филиал" ||
				selectedOrganization?.type.name === "Обособленное подразделение") &&
			requiredFields.type?.name === "Дочерняя организация"
		) {
			setRequiredFields((prev) => ({ ...prev, parent: null }));
		}
		//eslint-disable-next-line
	}, [requiredFields.type]);

	useEffect(() => {
		getSelectedData();
		//eslint-disable-next-line
	}, []);

	const getSelectedData = async () => {
		const newSelectedData = {
			parentList: [] as { id: any; name: string }[],
			hierarchyTypeList: [] as OrganizationTypeResponse[],
			typeList: [],
			activityAreaList: [] as ActivityTypeSimpleResponse[],
			CEOList: [] as { id: any; name: string }[],
		};

		await Promise.all([
			getActivityAreas().then((response) => {
				console.log(response)
				newSelectedData.activityAreaList = response;
			}),
			getPropertyTypes().then((response) => {
				newSelectedData.typeList = response;
			}),
			getEmployees().then((response) => {
				newSelectedData.CEOList = response;
			}),
			getOrganizationTypes().then((response) => {
				newSelectedData.hierarchyTypeList = response;
			}),
			getOrganizationParents().then((response) => {
				newSelectedData.parentList = response;
			}),
		]);
		setSelectData(newSelectedData);
	};

	async function getActivityAreas() {
		return await directoryAPI.activityAreasList().then(({ data }) => data);
		// return (await $api.get(`/directory/activity-areas`)).data;
	}

	async function getPropertyTypes() {
		return await directoryAPI
			.propertyTypesList()
			.then(({ data }: any) => data.map((_: any) => ({ id: _.id, name: _.type })));
		// return (await $api.get(`/directory/property-types`)).data.map((item: any) => ({ id: item.id, name: item.type }));
	}

	async function getEmployees() {
		return await directoryAPI.employeesList({ org_id: selectedOrganization!.id }).then(({ data }) =>
			data.map((item: any) => ({
				id: item.id,
				name: connectNames(item),
			})),
		);
		// return (await $api.get(`/directory/employees?org_id=${selectedOrganization!.id}`)).data.map((item: any) => ({
		// 	id: item.id,
		// 	name: connectNames(item),
		// }));
	}

	async function getOrganizationTypes() {
		return await directoryAPI
			.organizationTypesList({ id: selectedOrganization!.id, parent: selectedOrganization!.parent?.id })
			.then(({ data }) => data);
		// return (
		// 	await $api.get(
		// 		`/directory/organization-types?id=${selectedOrganization!.id}&parent=${selectedOrganization!.parent?.id}`,
		// 	)
		// ).data;
	}

	async function getOrganizationParents() {
		if (selectedOrganization?.type.positionIndex === 2 || selectedOrganization?.type.positionIndex === 3) {
			return await directoryAPI
				.organizationsParentList({ id: selectedOrganization.type.id })
				.then(({ data }) => data.map((item: any) => ({ id: item.id, name: item.shortName })));
			// return (await $api.get(`/directory/organizations/parent?id=${selectedOrganization.type.id}`)).data.map(
			// 	(item: any) => ({ id: item.id, name: item.shortName }),
			// );
		} else return [];
	}

	const submitHandler = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		if (Object.values(requiredFields).filter((item) => item).length !== Object.keys(requiredFields).length) {
			setSubmitError(true);
		} else {
			setSubmitError(false);
			setLoading(true);
			// date, securityDescription
			dispatch(
				editOrganization({
					organization: {
						...requiredFields,
						...optionalFields,
						phone: replacePhone(optionalFields.phone, "to_server"),
						securityDescription: selectedOrganization?.securityDescription || "",
						id: selectedOrganization!.id,
						registrationDate: selectedOrganization?.registrationDate || null,
					},
					setLoading,
					close,
				}),
			);
		}
	};

	const validate = (value: string, set: any) => {
		// const regExp = new RegExp(/^[^a-zA-Zа-яА-Я]+$/);
		// if (value.match(regExp) || value === "") {
		// 	set();
		// }

		set();
	};

	return (
		<section className={styles.wrp}>
			<h4 className={styles.title}>Редактирование карточки организации</h4>
			<form onSubmit={submitHandler} className={styles.form}>
				<div className={styles["form-wrp"]}>
					<div className={styles["form-item"]}>
						<div className={styles.item}>
							<MySelect
								disabled={selectedOrganization?.type.nestingIndex === 1 || !!selectedOrganization?.childrenCount}
								className={styles.item}
								list={selectData.hierarchyTypeList}
								value={requiredFields.type?.name || ""}
								set={(selected) => setRequiredFields((prev) => ({ ...prev, type: selected }))}
								name="Тип организации по иерархии"
								placeholder="Выберите тип организации"
								width="full"
								required={true}
								error={submitError}
								scroll={false}
								search={false}
							/>
						</div>
						<div className={styles.item}>
							<MySelect
								className={styles.item}
								list={selectData.typeList}
								value={optionalFields.propertyType?.name || ""}
								set={(selected) => setOptionalFields((prev) => ({ ...prev, propertyType: selected }))}
								name="Тип организации"
								placeholder="Выберите тип организации"
								width="full"
							/>
						</div>
						<div className={styles.item}>
							<MyInput
								name="Краткое название"
								placeholder="КЭАЗ"
								value={requiredFields.shortName}
								set={(e) => setRequiredFields((prev) => ({ ...prev, shortName: e.target.value }))}
								required={true}
								condition={submitError && !requiredFields.shortName.trim()}
								maxLength={150}
							/>
						</div>
						<div className={styles.item}>
							<MyTextarea
								className={styles["area-fullname"]}
								value={requiredFields.fullName}
								set={(e) => setRequiredFields((prev) => ({ ...prev, fullName: e.target.value }))}
								name="Полное название"
								placeholder="Общество с ограниченной ответственностью “Черноморская транспортная нефтяная компания Новороссийский филиал”"
								width="full"
								resize="vertical"
								required={true}
								error={submitError}
							/>
						</div>
						<div className={styles.item}>
							<InputTemp
								type="phone"
								value={optionalFields.phone}
								onChange={(e) => setOptionalFields((prev) => ({ ...prev, phone: e.target.value }))}
								label="Номер телефона"
								placeholder="+7 (000) 000–00–00"
							/>
						</div>
						<div className={styles.item}>
							<MyTextarea
								className={styles["area-fullname"]}
								value={optionalFields.legalAddress}
								set={(e) => setOptionalFields((prev) => ({ ...prev, legalAddress: e.target.value }))}
								name="Юридический адрес"
								placeholder="Улица Линии Октябрьской Железной Дороги 16к1"
								width="full"
								resize="vertical"
								maxLength={150}
							/>
						</div>
						<div className={styles.item}>
							<MyTextarea
								className={styles["area-fullname"]}
								value={optionalFields.postalAddress}
								set={(e) => setOptionalFields((prev) => ({ ...prev, postalAddress: e.target.value }))}
								name="Почтовый адрес"
								placeholder="Улица Линии Октябрьской Железной Дороги 16к1"
								width="full"
								resize="vertical"
								maxLength={150}
							/>
						</div>
					</div>
					<div className={styles["form-item"]}>
						<div className={styles.item}>
							<MyInput
								name="Ссылка на сайт"
								placeholder="https://yandex.ru"
								value={optionalFields.website}
								set={(e) => setOptionalFields((prev) => ({ ...prev, website: e.target.value }))}
							/>
						</div>
						<div className={styles.item}>
							<MySelect
								className={styles.item}
								list={selectData.activityAreaList}
								value={optionalFields.activityArea?.name || ""}
								set={(selected) => setOptionalFields((prev) => ({ ...prev, activityArea: selected }))}
								name="Сфера деятельности (по 187 ФЗ)"
								placeholder="Ракетно-космическая промышленность"
								width="full"
							/>
						</div>
						<div className={styles.item}>
							<MySelect
								className={styles.item}
								list={selectData.CEOList}
								value={optionalFields.responsible?.name || ""}
								set={(selected) => setOptionalFields((prev) => ({ ...prev, responsible: selected }))}
								name="Ответственный за информационную безопасность"
								placeholder="Константинопольский Константин Константинович"
								width="full"
							/>
						</div>
						{selectedOrganization?.type.nestingIndex !== 1 && !selectedOrganization?.childrenCount && (
							<div className={styles.item}>
								<MySelect
									disabled={
										!selectedOrganization?.childrenCount &&
										requiredFields.type?.name === "Дочерняя организация" &&
										selectedOrganization?.type.name === "Дочерняя организация"
									}
									className={styles.item}
									list={selectData.parentList}
									value={requiredFields.parent?.name || ""}
									set={(selected) => setRequiredFields((prev) => ({ ...prev, parent: selected }))}
									name="Родительская организация"
									placeholder="ОАО “Автоматика имени Курчатова”"
									width="full"
									required={true}
									error={submitError}
								/>
							</div>
						)}
						<div className={styles.item}>
							<MySelect
								className={styles.item}
								list={selectData.CEOList}
								value={optionalFields.director?.name || ""}
								set={(selected) => setOptionalFields((prev) => ({ ...prev, director: selected }))}
								name="Генеральный директор"
								placeholder="Константинопольский Константин Константинович"
								width="full"
							/>
						</div>
						<div className={styles.item}>
							<MyInput
								name="ИНН"
								placeholder="563565286576"
								value={optionalFields.inn}
								set={(e) =>
									validate(e.target.value, () => setOptionalFields({ ...optionalFields, inn: e.target.value }))
								}
								maxLength={INN_LIMIT}
							/>
						</div>
						<div className={styles.item}>
							<MyInput
								name="КПП"
								placeholder="7736 01 001"
								value={optionalFields.kpp}
								set={(e) =>
									validate(e.target.value, () => setOptionalFields({ ...optionalFields, kpp: e.target.value }))
								}
								maxLength={KPP_LIMIT}
							/>
						</div>
						<div className={styles.item}>
							<MyInput
								name="ОГРН"
								placeholder="1-02-66-05-60662-0"
								value={optionalFields.ogrn}
								set={(e) =>
									validate(e.target.value, () => setOptionalFields({ ...optionalFields, ogrn: e.target.value }))
								}
								maxLength={OGRN_LIMIT}
							/>
						</div>
					</div>
				</div>
				<div className={styles.btns}>
					<Button
						disabled={loading}
						onClick={close}
						type="button"
						variant={BUTTON_VARIANTS.OUTLINED}
						className={styles.button}>
						Отменить
					</Button>
					<Button loading={loading} type="submit" className={styles.button}>
						Готово
					</Button>
				</div>
			</form>
		</section>
	);
};
